import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { PhraseTextarea } from '@/components/journal/PhraseTextarea';
import { useReferrerIdentity } from '@/components/patient/useReferrerIdentity';
import { ApiCallError, callFunction } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  ABILITY_SCORES,
  DEFAULT_CONTAGION_PHRASES,
  DEFAULT_NURSING_PHRASES,
  NURSING_AREA_CODES,
  NURSING_AREA_I18N,
  XDIS21_TESTCENTER_EAN,
  isPsychiatrySpeciality,
  metaFromForm,
  selectableAbilityScores,
  signedByFromPractitioner,
  stateFromPrefill,
  withSignedByFallback,
  xdis21Issues,
  xdis21Payload,
  type AbilityElement,
  type CoordinationNeed,
  type DateTime,
  type DischargeDateCode,
  type NursingAreaCode,
  type Party,
  type Xdis21FormMeta,
  type Xdis21FormState,
} from '@/lib/xdis/xdis21Form';
import { FORM_ERROR_TYPE, FORM_HINT_TYPE, FORM_LABEL_TYPE } from '@/lib/typography';
import { useXdisSender } from './useXdisSender';
import { XdisReceiverChooser } from './XdisReceiverChooser';
import { chosenReceiver, emptyParty, type ReceiverChoice } from '@/lib/xdis/xdisReceiver';
import { XdisViewer } from './XdisViewer';

const FIELD =
  'mt-1 block w-full rounded-md border border-ds-field px-3 py-2 text-sm shadow-card focus:border-ds-teal focus:outline-none focus:ring-ds-teal   ';
const BOX = 'space-y-2 rounded-md border border-ds-line p-3 ';

type BuildResult = {
  id: string;
  ok: boolean;
  episodeOfCareIdentifier: string | null;
  checks: { name?: string; pass?: boolean; detail?: string }[];
  xml: string;
};

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className={FORM_LABEL_TYPE}>{label}</span>
      {children}
    </label>
  );
}

function DateTimeInput({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: DateTime;
  onChange: (v: DateTime) => void;
  testId: string;
}) {
  return (
    <fieldset className="grid grid-cols-2 gap-2" data-testid={testId}>
      <legend className={FORM_LABEL_TYPE}>{label}</legend>
      <input type="date" className={FIELD} value={value.date} onChange={(e) => onChange({ ...value, date: e.target.value })} aria-label={`${label} dato`} />
      <input type="time" className={FIELD} value={value.time} onChange={(e) => onChange({ ...value, time: e.target.value })} aria-label={`${label} klokkeslæt`} />
    </fieldset>
  );
}

function NeedRow({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: CoordinationNeed;
  onChange: (v: CoordinationNeed) => void;
  testId: string;
}) {
  const { t } = useTranslation();
  return (
    <div className={BOX} data-testid={testId}>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value.needed} onChange={(e) => onChange({ ...value, needed: e.target.checked })} />
        {label}
      </label>
      <textarea
        className={FIELD}
        rows={2}
        value={value.comment}
        onChange={(e) => onChange({ ...value, comment: e.target.value })}
        placeholder={t('patients.xdis.comment', 'Kommentar')}
      />
    </div>
  );
}

function PhraseField({
  clinicId,
  label,
  value,
  onChange,
  phrases,
  testId,
  rows = 2,
}: {
  clinicId: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  phrases: string[];
  testId: string;
  rows?: number;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid={testId}>
      <PhraseTextarea
        clinicId={clinicId}
        label={label}
        className={FIELD}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {phrases.length > 0 && (
        <Labelled label={t('patients.xdis.phrase_insert', 'Indsæt frase')}>
          <select
            className={FIELD}
            value=""
            onChange={(e) => {
              if (e.target.value) onChange(e.target.value);
            }}
          >
            <option value="">{t('patients.xdis.phrase_choose', 'Vælg frase')}</option>
            {phrases.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Labelled>
      )}
    </div>
  );
}

export function Xdis21Form({
  clinicId,
  patientId,
  patientName,
  sourceExchangeId,
  carePlanExchangeId,
  canRevealCpr = true,
  onClose,
}: {
  clinicId: string;
  patientId: string;
  patientName: string | null;
  sourceExchangeId: string;
  carePlanExchangeId?: string | null;
  canRevealCpr?: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const referrer = useReferrerIdentity(user?.id, clinicId);
  const { sender, gap } = useXdisSender(clinicId);
  const [form, setForm] = useState<Xdis21FormState | null>(null);
  const [meta, setMeta] = useState<Xdis21FormMeta | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<BuildResult | null>(null);
  const [defaultReceiver, setDefaultReceiver] = useState<Party | null>(null);
  const [receiverChoice, setReceiverChoice] = useState<ReceiverChoice>({ mode: 'default', party: null });

  useEffect(() => {
    let cancelled = false;
    callFunction<Record<string, unknown>, Record<string, unknown>>('communicate-xdis', {
      action: 'prefill21',
      clinic_id: clinicId,
      source_exchange_id: sourceExchangeId,
      ...(carePlanExchangeId ? { care_plan_exchange_id: carePlanExchangeId } : {}),
    }).then(
      (prefillData) => {
        if (cancelled) return;
        const raw = (prefillData.prefill ?? prefillData) as Record<string, unknown>;
        const state = stateFromPrefill(raw);
        setDefaultReceiver(state.receiver);
        setForm(state);
      },
      (err: unknown) =>
        !cancelled &&
        setLoadError(err instanceof ApiCallError ? err.message : t('patients.xdis.prefill21_failed', 'Indlæggelsesrapporten kunne ikke læses til plejeforløbsplanen.')),
    );
    return () => {
      cancelled = true;
    };
  }, [carePlanExchangeId, clinicId, sourceExchangeId, t]);

  const formReady = form !== null;
  useEffect(() => {
    if (!referrer.data) return;
    const fallback = signedByFromPractitioner(referrer.data.name, referrer.data.title);
    setForm((current) => (current ? withSignedByFallback(current, fallback) : current));
  }, [formReady, referrer.data]);

  useEffect(() => {
    let cancelled = false;
    const speciality = typeof sender?.medicalSpecialityCode === 'string' ? sender.medicalSpecialityCode : undefined;
    callFunction<Record<string, unknown>, Record<string, unknown>>('communicate-xdis', {
      action: 'form21',
      clinic_id: clinicId,
      ...(speciality ? { medical_speciality_code: speciality } : {}),
    })
      .catch(() => ({}))
      .then((formData) => {
        if (cancelled) return;
        setMeta(metaFromForm(formData, speciality));
      });
    return () => {
      cancelled = true;
    };
  }, [clinicId, sender?.medicalSpecialityCode]);

  if (loadError) return <p role="alert" className={FORM_ERROR_TYPE}>{loadError}</p>;
  if (!form) return <Loader2 className="h-5 w-5 animate-spin text-ds-muted" aria-label={t('patients.xdis.loading', 'Henter…')} />;

  const psychiatryVisible = meta?.psychiatryAdmissionPlanVisible ?? isPsychiatrySpeciality(sender?.medicalSpecialityCode);
  const scores = selectableAbilityScores(meta?.abilityScores ?? ABILITY_SCORES);
  const update = (patch: Partial<Xdis21FormState>) => setForm({ ...form, ...patch });
  const issues = xdis21Issues(form, { psychiatryVisible });

  async function onSubmit() {
    if (!form) return;
    setAttempted(true);
    if (issues.length > 0) {
      toast.error(t('patients.xdis.form_invalid', 'Rapporten er ikke dannet. Ret de markerede felter.'));
      return;
    }
    if (!sender) {
      toast.error(gap ?? t('patients.xdis.sender_loading', 'Klinikkens oplysninger er ikke indlæst endnu.'));
      return;
    }
    setPending(true);
    try {
      const built = await callFunction<Record<string, unknown>, BuildResult>('communicate-xdis', {
        action: 'build21',
        clinic_id: clinicId,
        patient_id: patientId,
        source_exchange_id: sourceExchangeId,
        ...(carePlanExchangeId ? { care_plan_exchange_id: carePlanExchangeId } : {}),
        plan: xdis21Payload(form, sender, { psychiatryVisible }),
      });
      setResult(built);
      await queryClient.invalidateQueries({ queryKey: ['hospital-referral-outbox', clinicId, patientId] });
      await queryClient.invalidateQueries({ queryKey: ['communicate-xdis-flow', clinicId, patientId] });
      await queryClient.invalidateQueries({ queryKey: ['communicate-xdis21', clinicId, patientId] });
      if (built.ok) toast.success(t('patients.xdis.built21', 'Plejeforløbsplanen er gemt og ligger i udbakken.'));
      else toast.error(t('patients.xdis.built21_failed_checks', 'Plejeforløbsplanen bestod ikke kontrollen og er ikke lagt i udbakken.'));
    } catch (err) {
      toast.error(err instanceof ApiCallError ? err.message : t('patients.xdis.build21_failed', 'Plejeforløbsplanen kunne ikke dannes.'));
    } finally {
      setPending(false);
    }
  }

  const err = (field: string) =>
    attempted
      ? issues.filter((i) => i.field === field).map((i) => (
          <p key={i.message} role="alert" className={FORM_ERROR_TYPE}>
            {i.message}
          </p>
        ))
      : null;

  const setNeed = (key: keyof Pick<
    Xdis21FormState,
    | 'homeChanges'
    | 'scheduleAuxilaries'
    | 'rehabilitationNeeds'
    | 'homeCareNeeds'
    | 'medicineAdministrationHelp'
    | 'medicineConsumptionHelp'
    | 'otherMunicipalWorkNeeded'
    | 'psychiatryAdmissionPlanNeeded'
  >) =>
    (v: CoordinationNeed) => update({ [key]: v });

  const setAbility = (i: number, patch: Partial<AbilityElement>) =>
    update({ abilityElements: form.abilityElements.map((el, j) => (j === i ? { ...el, ...patch } : el)) });

  return (
    <div className="space-y-4" data-testid="xdis21-form">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-base font-semibold">{t('patients.xdis.form21_title', 'Plejeforløbsplan (XDIS21)')}</h4>
        <Button type="button" size="sm" variant="secondary" onClick={onClose}>
          {t('patients.xdis.close', 'Luk')}
        </Button>
      </div>
      <p className={FORM_HINT_TYPE}>
        {t(
          'patients.xdis.form21_hint',
          'Udfyldt fra indlæggelsesrapporten. Patientens identifikation og forløbsnummer (EpisodeOfCareIdentifier) hentes fra den modtagne rapport og kan ikke ændres her.',
        )}
      </p>
      {gap && <p role="alert" className={FORM_ERROR_TYPE}>{gap}</p>}

      <div className={BOX} data-testid="xdis21-receiver">
        <XdisReceiverChooser
          fieldId="xdis21-receiver"
          choice={receiverChoice}
          onChange={(choice) => {
            setReceiverChoice(choice);
            update({ receiver: chosenReceiver(choice, defaultReceiver) ?? emptyParty() });
          }}
          defaultLabel={t('patients.xdis.receiver_default18', 'Indlæggelsesrapportens afsender')}
          defaultParty={defaultReceiver}
          testcenterEan={XDIS21_TESTCENTER_EAN}
          showErrors={attempted}
        />
        {err('receiver')}
      </div>

      <div className={BOX}>
        <span className="text-sm font-medium">{t('patients.xdis.signed_by', 'Underskrevet af')}</span>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Labelled label={t('patients.xdis.given_name', 'Fornavn')}>
            <input className={FIELD} maxLength={70} value={form.signedBy.givenName} onChange={(e) => update({ signedBy: { ...form.signedBy, givenName: e.target.value } })} />
          </Labelled>
          <Labelled label={t('patients.xdis.surname', 'Efternavn')}>
            <input className={FIELD} maxLength={70} value={form.signedBy.surname} onChange={(e) => update({ signedBy: { ...form.signedBy, surname: e.target.value } })} />
          </Labelled>
          <Labelled label={t('patients.xdis.title', 'Titel')}>
            <input className={FIELD} maxLength={25} value={form.signedBy.title} onChange={(e) => update({ signedBy: { ...form.signedBy, title: e.target.value } })} />
          </Labelled>
        </div>
        {err('signedBy')}
      </div>

      <div className={`${BOX} grid grid-cols-1 gap-3 sm:grid-cols-2`}>
        <div>
          <DateTimeInput label={t('patients.xdis.admission', 'Indlagt')} value={form.admission} onChange={(v) => update({ admission: v })} testId="xdis21-admission" />
          {err('admission')}
        </div>
        <div>
          <DateTimeInput
            label={t('patients.xdis.expected_end', 'Forventet udskrivnings-/færdigbehandlingsdato')}
            value={form.expectedEnd}
            onChange={(v) => update({ expectedEnd: v })}
            testId="xdis21-expected-end"
          />
          {err('expectedEnd')}
        </div>
        <div className="sm:col-span-2">
          <Labelled label={t('patients.xdis.expected_end_comment', 'Kommentar til forventet dato')}>
            <textarea className={FIELD} rows={2} value={form.expectedEndComment} onChange={(e) => update({ expectedEndComment: e.target.value })} data-testid="xdis21-expected-end-comment" />
          </Labelled>
          {err('expectedEndComment')}
        </div>
        <div>
          <DateTimeInput
            label={t('patients.xdis.agreed_discharge', 'Aftalt udskrivningsdato')}
            value={form.agreedDischarge}
            onChange={(v) => update({ agreedDischarge: v })}
            testId="xdis21-agreed-discharge"
          />
          {err('agreedDischarge')}
        </div>
        <Labelled label={t('patients.xdis.agreed_discharge_comment', 'Kommentar til aftalt udskrivningsdato')}>
          <textarea className={FIELD} rows={2} value={form.agreedDischargeComment} onChange={(e) => update({ agreedDischargeComment: e.target.value })} />
        </Labelled>
      </div>

      <div className={BOX}>
        <Labelled label={t('patients.xdis.cause', 'Indlæggelsesårsag')}>
          <textarea className={FIELD} rows={2} value={form.causeOfAdmission} onChange={(e) => update({ causeOfAdmission: e.target.value })} />
        </Labelled>
        {err('causeOfAdmission')}
        <Labelled label={t('patients.xdis.expected_course', 'Forventet indlæggelsesforløb')}>
          <textarea className={FIELD} rows={3} value={form.expectedCourseOfAdmission} onChange={(e) => update({ expectedCourseOfAdmission: e.target.value })} data-testid="xdis21-course" />
        </Labelled>
        <PhraseField
          clinicId={clinicId}
          label={t('patients.xdis.contagion', 'Smitterisiko')}
          value={form.riskOfContagion}
          onChange={(v) => update({ riskOfContagion: v })}
          phrases={meta?.phrases.riskOfContagion ?? DEFAULT_CONTAGION_PHRASES}
          testId="xdis21-contagion"
        />
      </div>

      <div className="space-y-2" data-testid="xdis21-coordination">
        <span className="text-sm font-medium">{t('patients.xdis.further_coordination', 'Yderligere koordinering')}</span>
        <NeedRow label={t('patients.xdis.home_changes', 'Behov for ændringer på bolig')} value={form.homeChanges} onChange={setNeed('homeChanges')} testId="xdis21-home-changes" />
        <NeedRow label={t('patients.xdis.auxiliaries', 'Hjælpemidler skal aftales')} value={form.scheduleAuxilaries} onChange={setNeed('scheduleAuxilaries')} testId="xdis21-auxiliaries" />
        <NeedRow label={t('patients.xdis.home_care', 'Behov for hjemmesygepleje')} value={form.homeCareNeeds} onChange={setNeed('homeCareNeeds')} testId="xdis21-home-care" />
        <NeedRow label={t('patients.xdis.medicine_admin', 'Behov for hjælp til medicinadministration')} value={form.medicineAdministrationHelp} onChange={setNeed('medicineAdministrationHelp')} testId="xdis21-medicine-admin" />
        <NeedRow label={t('patients.xdis.medicine_intake', 'Behov for hjælp til medicinindtagelse')} value={form.medicineConsumptionHelp} onChange={setNeed('medicineConsumptionHelp')} testId="xdis21-medicine-intake" />
        <NeedRow label={t('patients.xdis.other_municipal', 'Behov for anden kommunal indsats')} value={form.otherMunicipalWorkNeeded} onChange={setNeed('otherMunicipalWorkNeeded')} testId="xdis21-other-municipal" />
        {psychiatryVisible && (
          <NeedRow
            label={t('patients.xdis.psychiatry', 'Behov for psykiatri-udskrivningsaftale')}
            value={form.psychiatryAdmissionPlanNeeded}
            onChange={setNeed('psychiatryAdmissionPlanNeeded')}
            testId="xdis21-psychiatry"
          />
        )}
      </div>

      <div className={BOX} data-testid="xdis21-conference">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input type="checkbox" checked={form.conference.enabled} onChange={(e) => update({ conference: { ...form.conference, enabled: e.target.checked } })} />
          {t('patients.xdis.conference', 'Udskrivningskonference')}
        </label>
        {form.conference.enabled && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Labelled label={t('patients.xdis.conference_date_code', 'Dato er')}>
              <select
                className={FIELD}
                value={form.conference.dateCode}
                onChange={(e) => update({ conference: { ...form.conference, dateCode: e.target.value as DischargeDateCode } })}
                data-testid="xdis21-conference-date-code"
              >
                <option value="forslag">{t('patients.xdis.conference_proposal', 'Forslag')}</option>
                <option value="aftalt">{t('patients.xdis.conference_agreed', 'Aftalt')}</option>
              </select>
            </Labelled>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.conference.video}
                onChange={(e) => update({ conference: { ...form.conference, video: e.target.checked } })}
                data-testid="xdis21-conference-video"
              />
              {t('patients.xdis.conference_video', 'Holdes via video')}
            </label>
            <Labelled label={t('patients.xdis.conference_date', 'Dato for konference')}>
              <input type="date" className={FIELD} value={form.conference.date} onChange={(e) => update({ conference: { ...form.conference, date: e.target.value } })} />
            </Labelled>
            <Labelled label={t('patients.xdis.conference_time', 'Klokkeslæt')}>
              <input type="time" className={FIELD} value={form.conference.time} onChange={(e) => update({ conference: { ...form.conference, time: e.target.value } })} />
            </Labelled>
            <div className="sm:col-span-2">
              <Labelled label={t('patients.xdis.conference_comment', 'Kommentar til konference')}>
                <textarea className={FIELD} rows={2} value={form.conference.comment} onChange={(e) => update({ conference: { ...form.conference, comment: e.target.value } })} />
              </Labelled>
            </div>
          </div>
        )}
        {err('conference')}
      </div>

      <div className={BOX} data-testid="xdis21-ability">
        <span className="text-sm font-medium">{t('patients.xdis.ability', 'Funktionsevne ved udskrivning')}</span>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.abilityNotRelevant}
            onChange={(e) => update({ abilityNotRelevant: e.target.checked })}
            data-testid="xdis21-ability-not-relevant"
          />
          {t('patients.xdis.ability_not_relevant', 'Ikke relevant')}
        </label>
        <Labelled label={t('patients.xdis.ability_modified', 'Sidst ændret')}>
          <input type="date" className={FIELD} value={form.abilityLastModified} onChange={(e) => update({ abilityLastModified: e.target.value })} />
        </Labelled>
        {!form.abilityNotRelevant &&
          form.abilityElements.map((el, i) => (
            <div key={`${el.code}-${i}`} className="grid grid-cols-1 gap-2 border-t border-ds-line pt-2 sm:grid-cols-3">
              <Labelled label={t('patients.xdis.ability_item', 'Funktion')}>
                <input className={FIELD} value={el.codeDescription} onChange={(e) => setAbility(i, { codeDescription: e.target.value })} />
              </Labelled>
              <Labelled label={t('patients.xdis.ability_score', 'Score')}>
                <select className={FIELD} value={el.score} onChange={(e) => setAbility(i, { score: e.target.value })} data-testid="xdis21-ability-score">
                  <option value="">{t('patients.xdis.score_choose', 'Vælg score')}</option>
                  {scores.map((score) => (
                    <option key={score} value={score}>
                      {t(`patients.xdis.score_${score}`, score)}
                    </option>
                  ))}
                </select>
              </Labelled>
              <Labelled label={t('patients.xdis.comment', 'Kommentar')}>
                <input className={FIELD} value={el.comment} onChange={(e) => setAbility(i, { comment: e.target.value })} />
              </Labelled>
            </div>
          ))}
        {err('ability')}
      </div>

      <div className="space-y-2" data-testid="xdis21-nursing">
        <span className="text-sm font-medium">{t('patients.xdis.nursing', 'Sygeplejefaglige problemområder')}</span>
        {NURSING_AREA_CODES.map((code) => (
          <PhraseField
            key={code}
            clinicId={clinicId}
            label={t(`patients.xdis.${NURSING_AREA_I18N[code]}`, code)}
            value={form.nursing[code]}
            onChange={(v) => update({ nursing: { ...form.nursing, [code]: v } })}
            phrases={meta?.phrases.nursing[code as NursingAreaCode] ?? DEFAULT_NURSING_PHRASES[code]}
            testId={`xdis21-nursing-${code}`}
          />
        ))}
      </div>

      <div className="flex gap-2">
        <Button type="button" onClick={() => void onSubmit()} disabled={pending || Boolean(gap)} data-testid="xdis21-submit">
          {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {t('patients.xdis.build21', 'Gem plejeforløbsplan')}
        </Button>
      </div>

      {result && (
        <div className="space-y-2" data-testid="xdis21-result">
          <ul className="space-y-1 text-sm">
            {result.checks.map((c, i) => (
              <li key={i} className={c.pass === false ? 'text-ds-red-ink' : 'text-ds-green-ink '}>
                {c.pass === false ? '✗' : '✓'} {c.name} {c.detail ? `— ${c.detail}` : ''}
              </li>
            ))}
          </ul>
          {result.xml && <XdisViewer xml={result.xml} kind="XDIS21" patientId={patientId} patientName={patientName} canRevealCpr={canRevealCpr} />}
        </div>
      )}
    </div>
  );
}
